// P12b + P15b — Dialog de confirmation réutilisable
// Remplace window.confirm() natif par un dialog shadcn-style.
// Supporte 2 modes :
//   - "always-open" (legacy) : <ConfirmDialog onConfirm={...} onCancel={...} /> toujours rendu
//   - "controlled" : <ConfirmDialog open={x !== null} onConfirm={...} onCancel={...} />
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ht } from "@/lib/ht";

export interface ConfirmDialogProps {
  /** Si fourni, le dialog s'affiche seulement si open===true (controlled mode). */
  open?: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
  /** @deprecated use `loading` */
  pending?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirmer",
  cancelLabel = ht("Annuler"),
  variant = "default",
  pending = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Si `open` est fourni, on respecte le controlled mode.
  // Sinon, on est toujours visible (legacy).
  if (open === false) return null;
  const isLoading = loading || pending;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(28rem,calc(100vw-2rem))]">
        <CardHeader>
          <div className="flex items-start gap-3">
            {variant === "destructive" && (
              <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
            )}
            <CardTitle className="text-base">{title}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{description}</p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel} disabled={isLoading}>
              {cancelLabel}
            </Button>
            <Button
              variant={variant === "destructive" ? "destructive" : "default"}
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? "En cours..." : confirmLabel}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
